// src/pages/misc/landing/contrast/OtherCrmVisuals.tsx
// "Hoy / Los demás" side — fragments of OTHER tools, lifted off their pages with no shell:
// clashing brand colours, flat grey tables, a bolt-on chatbot. Deliberately not our style.
// All motion is CSS in contrast.css, driven by --vl-swap on the section.
//
// [HANDOFF] The three "Hoy / Los demás" pictures, left-hand card of each pinned beat: AppChaos (beat c1, six app icons with
//   tangled arrows), BoltOnAi (beat ia, a bolted-on chatbot) and TenAgents (beat money, ten avatars). They are intentionally ugly
//   (clashing brand colours) so the right-hand card looks better by contrast. Do not "clean them up". The colours on purpose are
//   WhatsApp green, phone blue, Excel green, Gmail red, CRM purple, sticky-note yellow. No state, no timers; motion is CSS only.
import React from 'react';
import {
  FileSpreadsheet,
  Mail,
  MessageCircle,
  Phone,
  Sparkles,
  StickyNote,
  Table2,
} from 'lucide-react';

/* ---------- 1 · Caos de apps ---------------------------------------------- */

const APPS = [
  { k: 'wa', Icon: MessageCircle, bg: '#25D366', fg: '#0b3d22', badge: 12, x: 6, y: 10, r: -6 },
  { k: 'tel', Icon: Phone, bg: '#2F80ED', fg: '#fff', badge: 3, x: 36, y: 2, r: 4 },
  { k: 'xls', Icon: FileSpreadsheet, bg: '#1D6F42', fg: '#d7f5e2', badge: 0, x: 66, y: 12, r: -3 },
  { k: 'mail', Icon: Mail, bg: '#EA4335', fg: '#fff', badge: 47, x: 12, y: 58, r: 5 },
  { k: 'crm', Icon: Table2, bg: '#6B4EFF', fg: '#fff', badge: 0, x: 44, y: 52, r: -4 },
  { k: 'note', Icon: StickyNote, bg: '#FFD83D', fg: '#5c4a00', badge: 0, x: 72, y: 60, r: 7 },
] as const;

export const AppChaos: React.FC = () => (
  <div className="vl-chaos2">
    <svg className="vl-chaos2-arrows" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
      <defs>
        <marker id="vl-arr" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
          <path d="M0 0 L10 5 L0 10 z" fill="rgba(255,255,255,0.45)" />
        </marker>
      </defs>
      {[
        'M16 22 C 30 10, 40 4, 44 12',
        'M52 14 C 62 6, 70 10, 72 22',
        'M74 32 C 78 44, 72 56, 62 62',
        'M52 64 C 40 70, 30 70, 22 66',
        'M18 58 C 10 48, 10 36, 16 30',
        'M46 24 C 50 36, 46 44, 50 52',
      ].map((d, i) => (
        <path key={i} d={d} className="vl-chaos2-arrow" style={{ animationDelay: `${i * 0.35}s` }} markerEnd="url(#vl-arr)" />
      ))}
    </svg>
    {APPS.map((a, i) => (
      <div
        key={a.k}
        className="vl-chaos2-app"
        style={{
          left: `${a.x}%`,
          top: `${a.y}%`,
          background: a.bg,
          color: a.fg,
          ['--r' as string]: `${a.r}deg`,
          animationDelay: `${i * 0.27}s`,
        }}
      >
        <a.Icon size={26} strokeWidth={2} />
        {a.badge > 0 && <span className="vl-chaos2-badge">{a.badge}</span>}
      </div>
    ))}
    <div className="vl-chaos2-caption">copiar · pegar · copiar · pegar</div>
  </div>
);

/* ---------- 2 · IA pegada a un CRM viejo ---------------------------------- */

const ROWS = [
  ['Ana Gutiérrez', 'New', 'jperez', '—'],
  ['Carlos Méndez', 'Open', '—', '12 días'],
  ['Luis Hernández', 'Open', 'mlopez', '—'],
  ['Sofía Vargas', 'Lost', '—', '31 días'],
];

export const BoltOnAi: React.FC = () => (
  <div className="vl-boltai">
    <div className="vl-boltai-table">
      <div className="vl-boltai-head">
        <span>LEADS_EXPORT_v2.3.xlsx</span>
        <span className="vl-boltai-sync">sync: ayer 03:14</span>
      </div>
      <div className="vl-boltai-row vl-boltai-th">
        <span>Nombre</span>
        <span>Status</span>
        <span>Owner</span>
        <span>Últ. contacto</span>
      </div>
      {ROWS.map((r) => (
        <div key={r[0]} className="vl-boltai-row">
          {r.map((c, j) => (
            <span key={j} className={c === '—' ? 'vl-boltai-empty' : undefined}>
              {c}
            </span>
          ))}
        </div>
      ))}
    </div>
    <div className="vl-boltai-bot">
      <div className="vl-boltai-bot-head">
        <Sparkles size={14} />
        AI Assistant <span className="vl-boltai-beta">BETA</span>
      </div>
      <div className="vl-boltai-bubble">
        Lo siento, no entendí tu pregunta. ¿Puedes reformularla?
      </div>
      <div className="vl-boltai-bubble vl-boltai-bubble-user">¿Qué lead sigue?</div>
      <div className="vl-boltai-typing">
        <i />
        <i />
        <i />
      </div>
    </div>
  </div>
);

/* ---------- 3 · Diez asesores --------------------------------------------- */

const AGENTS = [
  ['JP', '#8d6e63', 14],
  ['ML', '#78909c', 9],
  ['RG', '#a1887f', 22],
  ['SC', '#90a4ae', 6],
  ['DF', '#9e9e9e', 17],
  ['AV', '#bcaaa4', 11],
  ['TR', '#b0bec5', 25],
  ['LM', '#a5a5a5', 8],
  ['PC', '#8c9aa5', 19],
  ['KB', '#7f8c8d', 13],
] as const;

export const TenAgents: React.FC = () => (
  <div className="vl-ten">
    <div className="vl-ten-grid">
      {AGENTS.map(([ini, bg, n], i) => (
        <div key={ini} className="vl-ten-agent" style={{ ['--i' as string]: i }}>
          <span className="vl-ten-avatar" style={{ background: bg }}>
            {ini}
          </span>
          <span className="vl-ten-wait">{n}</span>
          <span className="vl-ten-pay">$</span>
        </div>
      ))}
    </div>
    <div className="vl-ten-sheet">
      <span>Nómina</span>
      <span>10 sueldos</span>
      <span className="vl-ten-mill">$500 mil<br />en comisiones</span>
      <span className="vl-ten-red">144 leads sin contestar</span>
    </div>
  </div>
);
